import { useState } from "react";
import "./AddLinkFlow.scss";

function AddLinkFlow({
  onSelectList,
  onSelectNote,
  onAddExternalLink,
  onClose,
  isUpdating,
  initialScreen = "type",
}) {
  const [screen, setScreen] = useState(initialScreen); // 'type' | 'listChoice' | 'noteChoice' | 'externalLink' | 'email'
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [infoText, setInfoText] = useState("");
  const [infoType, setInfoType] = useState("");

  const goBack = () => {
    setScreen("type");
    setLinkName("");
    setLinkUrl("");
    setInfoText("");
    setInfoType("");
  };

  const handleSubmitLink = () => {
    onAddExternalLink(linkName, linkUrl, (text, type) => {
      setInfoText(text);
      setInfoType(type);
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isUpdating) {
      handleSubmitLink();
    }
  };

  // ── Screen: type selection ──────────────────────────────────
  if (screen === "type") {
    return (
      <div className="add-link-flow">
        <div className="link-type-grid">
          <button
            className="link-type-card"
            onClick={() => setScreen("listChoice")}
          >
            <div className="card-icon list-icon">
              <span className="material-symbols-outlined">list</span>
            </div>
            <div className="card-content">
              <h4>List</h4>
              <p>Create or link a to-do list</p>
            </div>
          </button>

          <button
            className="link-type-card"
            onClick={() => setScreen("noteChoice")}
          >
            <div className="card-icon note-icon">
              <span className="material-symbols-outlined">note</span>
            </div>
            <div className="card-content">
              <h4>Note</h4>
              <p>Create or link a note</p>
            </div>
          </button>

          <button
            className="link-type-card"
            onClick={() => setScreen("externalLink")}
          >
            <div className="card-icon link-icon">
              <span className="material-symbols-outlined">link</span>
            </div>
            <div className="card-content">
              <h4>External Link</h4>
              <p>Add a URL to an external resource</p>
            </div>
          </button>

          <button className="link-type-card" onClick={() => setScreen("email")}>
            <div className="card-icon email-icon">
              <span className="material-symbols-outlined">mail</span>
            </div>
            <div className="card-content">
              <h4>Email</h4>
              <p>Add an email address</p>
            </div>
          </button>
        </div>
      </div>
    );
  }

  // ── Screen: list choice ─────────────────────────────────────
  if (screen === "listChoice") {
    return (
      <div className="add-link-flow choice-screen">
        <button className="back-btn" onClick={goBack}>
          <span className="material-symbols-outlined">arrow_back</span>
          Back
        </button>
        <h3 className="flow-title">Link List</h3>
        <div className="choice-buttons">
          <button
            className="choice-btn primary"
            onClick={() => onSelectList("create")}
          >
            <span className="material-symbols-outlined">add_circle</span>
            Create New List
          </button>
          <button
            className="choice-btn secondary"
            onClick={() => onSelectList("link")}
          >
            <span className="material-symbols-outlined">link</span>
            Link Existing List
          </button>
        </div>
      </div>
    );
  }

  // ── Screen: note choice ─────────────────────────────────────
  if (screen === "noteChoice") {
    return (
      <div className="add-link-flow choice-screen">
        <button className="back-btn" onClick={goBack}>
          <span className="material-symbols-outlined">arrow_back</span>
          Back
        </button>
        <h3 className="flow-title">Link Note</h3>
        <div className="choice-buttons">
          <button
            className="choice-btn primary"
            onClick={() => onSelectNote("create")}
          >
            <span className="material-symbols-outlined">add_circle</span>
            Create New Note
          </button>
          <button
            className="choice-btn secondary"
            onClick={() => onSelectNote("link")}
          >
            <span className="material-symbols-outlined">link</span>
            Link Existing Note
          </button>
        </div>
      </div>
    );
  }

  // ── Shared form for link-type screens ───────────────────────
  const renderLinkForm = ({
    flowTitle,
    urlLabel,
    urlPlaceholder,
    urlInputType,
    screenClass,
  }) => (
    <div className={`add-link-flow ${screenClass}`}>
      <button className="back-btn" onClick={goBack} disabled={isUpdating}>
        <span className="material-symbols-outlined">arrow_back</span>
        Back
      </button>
      <h3 className="flow-title">{flowTitle}</h3>
      <div className="form-content">
        <div className="input-group">
          <label htmlFor="flow-link-name">Name (optional)</label>
          <input
            id="flow-link-name"
            type="text"
            placeholder="Friendly name"
            value={linkName}
            onChange={(e) => {
              setLinkName(e.target.value);
              setInfoText("");
            }}
            onKeyPress={handleKeyPress}
            className="input-field"
          />
        </div>
        <div className="input-group">
          <label htmlFor="flow-link-url">{urlLabel}</label>
          <input
            id="flow-link-url"
            type={urlInputType}
            placeholder={urlPlaceholder}
            value={linkUrl}
            onChange={(e) => {
              setLinkUrl(e.target.value);
              setInfoText("");
            }}
            onKeyPress={handleKeyPress}
            className="input-field"
            autoFocus
          />
        </div>
        {infoText && (
          <div className={`info-message ${infoType}`}>{infoText}</div>
        )}
        <div className="form-actions">
          <button
            className="cancel-btn"
            onClick={onClose}
            disabled={isUpdating}
          >
            Cancel
          </button>
          <button
            className={`add-btn ${isUpdating ? "updating" : ""}`}
            onClick={handleSubmitLink}
            disabled={isUpdating}
          >
            <span className="material-symbols-outlined">add</span>
            {isUpdating ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Screen: external link form ──────────────────────────────
  if (screen === "externalLink") {
    return renderLinkForm({
      flowTitle: "Link External",
      urlLabel: "URL",
      urlPlaceholder: "https://example.com",
      urlInputType: "text",
      screenClass: "external-link-screen",
    });
  }

  // ── Screen: email form ──────────────────────────────────────
  if (screen === "email") {
    return renderLinkForm({
      flowTitle: "Link Email",
      urlLabel: "Email Address",
      urlPlaceholder: "user@example.com",
      urlInputType: "email",
      screenClass: "email-screen",
    });
  }

  return null;
}

export default AddLinkFlow;
